import { fetchWithAuth } from "./fetchWithAuth";

const API_BASE = process.env.REACT_APP_API_BASE;

const requireWeddingId = (weddingId) => {
  if (!weddingId) {
    throw new Error("Missing wedding id.");
  }
};

const buildFinanceBase = (weddingId) => {
  requireWeddingId(weddingId);
  return `${API_BASE}/weddings/${encodeURIComponent(String(weddingId))}/finance`;
};

const buildQuery = (params = {}) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    search.append(key, String(value));
  });

  const query = search.toString();
  return query ? `?${query}` : "";
};

const parseJsonResponse = async (
  response,
  fallbackMessage,
  { allowNotFound = false } = {}
) => {
  if (!response) {
    throw new Error(fallbackMessage);
  }

  if (allowNotFound && response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || fallbackMessage);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

const requestJson = async (
  url,
  {
    method = "GET",
    body,
    fallbackMessage = "Finance request failed.",
    allowNotFound = false,
  } = {}
) => {
  const response = await fetchWithAuth(url, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  return parseJsonResponse(response, fallbackMessage, { allowNotFound });
};

const requestFormData = async (url, formData, fallbackMessage) => {
  const response = await fetchWithAuth(url, {
    method: "POST",
    body: formData,
  });

  return parseJsonResponse(response, fallbackMessage);
};

export function getWeddingFinanceBudget(weddingId, { allowNotFound = true } = {}) {
  return requestJson(`${buildFinanceBase(weddingId)}/budget`, {
    method: "GET",
    fallbackMessage: "Failed to load budget.",
    allowNotFound,
  });
}

export function createWeddingFinanceBudget(weddingId, payload) {
  return requestJson(`${buildFinanceBase(weddingId)}/budget`, {
    method: "POST",
    body: payload,
    fallbackMessage: "Failed to create budget.",
  });
}

export function updateWeddingFinanceBudget(weddingId, payload) {
  return requestJson(`${buildFinanceBase(weddingId)}/budget`, {
    method: "PATCH",
    body: payload,
    fallbackMessage: "Failed to update budget.",
  });
}

export function getWeddingFinanceCategories(weddingId) {
  return requestJson(`${buildFinanceBase(weddingId)}/categories`, {
    method: "GET",
    fallbackMessage: "Failed to load categories.",
  });
}

export function createWeddingFinanceCategory(weddingId, payload) {
  return requestJson(`${buildFinanceBase(weddingId)}/categories`, {
    method: "POST",
    body: payload,
    fallbackMessage: "Failed to create category.",
  });
}

export function updateWeddingFinanceCategory(weddingId, categoryId, payload) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/categories/${encodeURIComponent(
      String(categoryId)
    )}`,
    {
      method: "PATCH",
      body: payload,
      fallbackMessage: "Failed to update category.",
    }
  );
}

export function getWeddingFinanceQuotes(weddingId, filters = {}) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/quotes${buildQuery(filters)}`,
    {
      method: "GET",
      fallbackMessage: "Failed to load quotes.",
    }
  );
}

export function createWeddingFinanceQuote(weddingId, payload) {
  return requestJson(`${buildFinanceBase(weddingId)}/quotes`, {
    method: "POST",
    body: payload,
    fallbackMessage: "Failed to create quote.",
  });
}

export function updateWeddingFinanceQuote(weddingId, quoteId, payload) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/quotes/${encodeURIComponent(String(quoteId))}`,
    {
      method: "PATCH",
      body: payload,
      fallbackMessage: "Failed to update quote.",
    }
  );
}

export function getWeddingFinancePayments(weddingId, filters = {}) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/payments${buildQuery(filters)}`,
    {
      method: "GET",
      fallbackMessage: "Failed to load payments.",
    }
  );
}

export function createWeddingFinancePayment(weddingId, payload) {
  return requestJson(`${buildFinanceBase(weddingId)}/payments`, {
    method: "POST",
    body: payload,
    fallbackMessage: "Failed to create payment.",
  });
}

export function updateWeddingFinancePayment(weddingId, paymentId, payload) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/payments/${encodeURIComponent(
      String(paymentId)
    )}`,
    {
      method: "PATCH",
      body: payload,
      fallbackMessage: "Failed to update payment.",
    }
  );
}

export function getWeddingFinanceExpenses(weddingId, filters = {}) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/expenses${buildQuery(filters)}`,
    {
      method: "GET",
      fallbackMessage: "Failed to load expenses.",
    }
  );
}

export function createWeddingFinanceExpense(weddingId, payload) {
  return requestJson(`${buildFinanceBase(weddingId)}/expenses`, {
    method: "POST",
    body: payload,
    fallbackMessage: "Failed to create expense.",
  });
}

export function updateWeddingFinanceExpense(weddingId, expenseId, payload) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/expenses/${encodeURIComponent(
      String(expenseId)
    )}`,
    {
      method: "PATCH",
      body: payload,
      fallbackMessage: "Failed to update expense.",
    }
  );
}

export function getWeddingFinanceAttachments(weddingId, filters = {}) {
  return requestJson(
    `${buildFinanceBase(weddingId)}/attachments${buildQuery(filters)}`,
    {
      method: "GET",
      fallbackMessage: "Failed to load attachments.",
    }
  );
}

export function createWeddingFinanceAttachment(weddingId, payload) {
  const formData = new FormData();
  formData.append("entity_type", payload?.entity_type || "");

  if (payload?.expense_id) {
    formData.append("expense_id", String(payload.expense_id));
  }
  if (payload?.vendor_quote_id) {
    formData.append("vendor_quote_id", String(payload.vendor_quote_id));
  }
  if (payload?.payment_id) {
    formData.append("payment_id", String(payload.payment_id));
  }
  if (payload?.file) {
    formData.append("file", payload.file);
  }

  return requestFormData(
    `${buildFinanceBase(weddingId)}/attachments`,
    formData,
    "Failed to create attachment."
  );
}
